import React, { useId } from "react";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import FormLabel from "./FormLabel";
import { useTranslation } from "react-i18next";
import InputError from "./InputError";
import clsx from "clsx";
import { CalendarIcon } from "@heroicons/react/outline";

const FormDateRange = ({
  label,
  id: propId,
  onChange,
  startDate,
  endDate,
  required = false,
  dateFormat = "dd/MM/yyyy",
  hint,
  error,
  onClear,
  disabled,
  ...props
}) => {
  const { t } = useTranslation();

  const generatedId = useId();
  const id = propId || generatedId;

  const hasError = Boolean(
    error?.message || error?.messages?.length || error?.length,
  );

  const inputClass = clsx(
    "block w-full rounded-md shadow-sm text-sm text-gray-900 placeholder:text-gray-400 border-0 ring-1 focus:ring-2 focus:ring-inset disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 disabled:ring-gray-200 pl-10",
    hasError
      ? "ring-red-300 focus:ring-red-500"
      : "ring-gray-300 focus:ring-blue-500",
  );

  const showClear = Boolean(startDate || endDate) && onClear;

  return (
    <div>
      {Boolean(label) && (
        <FormLabel htmlFor={id} required={required} hint={hint}>
          {t(label)}
        </FormLabel>
      )}
      <div className="relative">
        <ReactDatePicker
          className={inputClass}
          selectsRange={true}
          startDate={startDate}
          endDate={endDate}
          onChange={onChange}
          dateFormat={dateFormat}
          disabled={disabled}
          {...props}
        />
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <CalendarIcon className="h-5 w-5 text-gray-500" aria-hidden="true" />
        </div>
        {showClear && !disabled && (
          <div className={"absolute inset-y-0 right-0 pr-2 flex items-center"}>
            <button
              type="button"
              onClick={onClear}
              className="text-gray-500 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 focus:text-gray-700"
            >
              <span className="sr-only">{t("common.clear")}</span>
              <svg
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
      {hasError && <InputError id={id} error={error} />}
    </div>
  );
};

export default FormDateRange;
